import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    IconButton,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Grid,
    CircularProgress,
    Alert,
    Tooltip,
    Badge,
    Card,
    CardHeader,
    CardContent,
    Tabs,
    Tab,
    Stack,
    Container,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Fade,
    Collapse,
    useTheme,
    useMediaQuery,
    Fab,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon
} from '@mui/material';
import {
    Description as DescriptionIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Download as DownloadIcon,
    Visibility as VisibilityIcon,
    FileCopy as FileCopyIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    WarningAmber as WarningAmberIcon,
    FileOpen as FileOpenIcon,
    Photo as PhotoIcon,
    PictureAsPdf as PdfIcon,
    Upload as UploadIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    Save as SaveIcon,
    FilterList as FilterListIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import truckDocumentService from '../services/truckDocumentService';
import { format, isValid, parseISO } from 'date-fns';
import TableSkeleton from './skeleton/TableSkeleton';
import { useAuth } from '../context/AuthContext';

// Document types will be fetched from backend to match server enums

// Helper function to determine document icon by file type
const getDocumentIcon = (fileReference) => {
    if (!fileReference) return <DescriptionIcon />;
    
    const ext = fileReference.split('.').pop().toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
        return <PhotoIcon />;
    } else if (ext === 'pdf') {
        return <PdfIcon />;
    } else if (['doc', 'docx'].includes(ext)) {
        return <FileOpenIcon />;
    }
    
    return <DescriptionIcon />;
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseISO(dateString);
    return isValid(date) ? format(date, 'MM/dd/yyyy') : 'Invalid Date';
};

// Color mapping for document status
const statusColors = {
    'VALID': 'success',
    'EXPIRING_SOON': 'warning', 
    'EXPIRED': 'error',
    'INVALID': 'error'
};

// Document status icon mapping
const statusIcons = {
    'VALID': <CheckCircleIcon fontSize="small" />,
    'EXPIRING_SOON': <WarningAmberIcon fontSize="small" />,
    'EXPIRED': <WarningIcon fontSize="small" />,
    'INVALID': <WarningIcon fontSize="small" />
};

function TruckDocumentList({ truckId, truck }) {
    const { user } = useAuth();
    const canManageDocs = user?.role === 'admin' || user?.role === 'company_admin';
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    // Form state
    const [formData, setFormData] = useState({
        truck: truckId,
        documentType: '',
        displayName: '',
        issueDate: null,
        expiryDate: null,
        issuedBy: '',
        documentNumber: '',
        notes: ''
    });
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Form steps for better UX
    const steps = [
        {
            label: 'Document Type',
            description: 'Select the type of document you want to upload'
        },
        {
            label: 'Document Details',
            description: 'Provide document information and dates'
        },
        {
            label: 'Upload File',
            description: 'Upload the document file'
        }
    ];

    // Fetch documents and document types on component mount
    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const [docsResponse, typesResponse] = await Promise.all([
                    truckDocumentService.getTruckDocuments(truckId),
                    truckDocumentService.getDocumentTypes()
                ]);
                if (!mounted) return;
                const docs = docsResponse.data || [];
                setDocuments(docs);
                try {
                    console.group('[TruckDocumentList] Documents fetched');
                    console.log('count:', Array.isArray(docs) ? docs.length : 'n/a');
                    (docs || []).slice(0, 5).forEach((d, i) => console.log(`#${i}`, { id: d?._id, type: d?.documentType, displayName: d?.displayName, fileReference: d?.fileReference }));
                    console.groupEnd();
                } catch (_) {}
                const types = Array.isArray(typesResponse?.data) ? typesResponse.data : [];
                // Normalize into id/label
                setDocumentTypes(types.map(t => ({ id: t.value, label: t.name })));
            } catch (err) {
                console.error('Error loading truck documents or types:', err);
                if (mounted) setError('Failed to load documents. Please try again.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        
        if (truckId) {
            fetchData();
        }
        return () => { mounted = false; };
    }, [truckId]);

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Auto-fill display name based on document type
        if (name === 'documentType') {
            const docType = documentTypes.find(type => type.id === value);
            if (docType && !formData.displayName) {
                setFormData(prev => ({ ...prev, displayName: docType.label }));
            }
        }
    };
    
    // Handle date changes
    const handleDateChange = (name, date) => {
        setFormData(prev => ({ ...prev, [name]: date }));
    };
    
    // Handle file selection with drag and drop
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setSelectedFile(e.dataTransfer.files[0]);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            truck: truckId,
            documentType: '',
            displayName: '',
            issueDate: null,
            expiryDate: null,
            issuedBy: '',
            documentNumber: '',
            notes: ''
        });
        setSelectedFile(null);
        setActiveStep(0);
    };

    // Open form to add a new document
    const handleAdd = () => {
        if (!canManageDocs) return;
        resetForm();
        setFormMode('add');
        setOpenForm(true);
    };

    // Open form to edit an existing document
    const handleEdit = (document) => {
        if (!canManageDocs) return;
        setFormData({
            truck: truckId,
            documentType: document.documentType,
            displayName: document.displayName,
            issueDate: document.issueDate ? new Date(document.issueDate) : null,
            expiryDate: document.expiryDate ? new Date(document.expiryDate) : null,
            issuedBy: document.issuedBy || '',
            documentNumber: document.documentNumber || '',
            notes: document.notes || ''
        });
        setSelectedDocument(document);
        setSelectedFile(null);
        setFormMode('edit');
        setActiveStep(1); // Skip document type selection for editing
        setOpenForm(true);
    };

    // Handle form submission with better error handling
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canManageDocs) return;
        setSubmitting(true);
        
        try {
            // Prepare payload matching server expectations
            const payload = {
                truck: truckId,
                documentType: formData.documentType,
                displayName: formData.displayName,
                issuedBy: formData.issuedBy,
                documentNumber: formData.documentNumber,
                notes: formData.notes,
                // Convert dates to ISO strings if present
                issueDate: formData.issueDate instanceof Date ? formData.issueDate.toISOString() : formData.issueDate,
                expiryDate: formData.expiryDate instanceof Date ? formData.expiryDate.toISOString() : formData.expiryDate
            };

            let response;
            if (formMode === 'add') {
                response = await truckDocumentService.createDocument(payload, selectedFile);
            } else {
                response = await truckDocumentService.updateDocument(selectedDocument._id, payload, selectedFile);
            }
            
            if (response.data) {
                try {
                    console.log('[TruckDocumentList] Save response data:', response.data);
                } catch (_) {}
                // Refresh documents list
                const docsResponse = await truckDocumentService.getTruckDocuments(truckId);
                const docs = docsResponse.data || [];
                setDocuments(docs);
                try {
                    console.group('[TruckDocumentList] Documents after save');
                    console.log('count:', Array.isArray(docs) ? docs.length : 'n/a');
                    (docs || []).slice(0, 5).forEach((d, i) => console.log(`#${i}`, { id: d?._id, type: d?.documentType, displayName: d?.displayName, fileReference: d?.fileReference }));
                    console.groupEnd();
                } catch (_) {}
                
                setOpenForm(false);
                resetForm();
            }
        } catch (err) {
            console.error('Error saving document:', err);
            setError('Failed to save document. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle document deletion
    const handleDeleteConfirm = (document) => {
        if (!canManageDocs) return;
        setDeleteTarget(document);
        setConfirmDelete(true);
    };

    const handleDelete = async () => {
        if (!canManageDocs || !deleteTarget) return;
        try {
            await truckDocumentService.deleteDocument(deleteTarget._id);
            
            // Refresh documents list
            const docsResponse = await truckDocumentService.getTruckDocuments(truckId);
            setDocuments(docsResponse.data || []);
            
            setConfirmDelete(false);
            setDeleteTarget(null);
        } catch (err) {
            console.error('Error deleting document:', err);
            setError('Failed to delete document. Please try again.');
        }
    };

    // Preview via authenticated signed URL (S3) or content endpoint (legacy disk)
    const handlePreview = async (document) => {
        setPreviewDocument(document);
        setPreviewOpen(true);
        setPreviewUrl('');
        if (!document?._id || (!document.fileReference && !document.s3Key)) {
            return;
        }
        setPreviewLoading(true);
        try {
            const res = await truckDocumentService.getDocumentUrl(document._id);
            setPreviewUrl(res?.data?.url || '');
        } catch (err) {
            console.error('[TruckDocumentList] Failed to resolve document URL', err);
            setError(err?.response?.data?.message || 'Could not open document file');
        } finally {
            setPreviewLoading(false);
        }
    };

    const isImageDoc = (doc, url) => {
        const ct = String(doc?.contentType || '').toLowerCase();
        if (ct.startsWith('image/')) return true;
        const name = doc?.originalFilename || doc?.fileReference || url || '';
        return /\.(png|jpe?g|gif|webp)$/i.test(name);
    };
    // Filter documents based on search and filters
    const filteredDocuments = documents.filter(doc => {
        const matchesSearch = doc.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            doc.documentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            doc.issuedBy?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === 'all' || doc.status === filterStatus;
        const matchesType = filterType === 'all' || doc.documentType === filterType;
        
        return matchesSearch && matchesStatus && matchesType;
    });

    // Get next step for form
    const handleNext = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    // Check if current step is valid
    const isStepValid = (step) => {
        switch (step) {
            case 0:
                return formData.documentType !== '';
            case 1:
                return formData.displayName !== '';
            case 2:
                return selectedFile !== null || formMode === 'edit';
            default:
                return false;
        }
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <TableSkeleton rows={6} showHeader={true} showActions={true} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
            {/* Header with improved mobile layout */}
            <Paper 
                sx={{ 
                    p: { xs: 2, md: 3 }, 
                    mb: 3, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    color: 'white'
                }}
            >
                <Stack 
                    direction={{ xs: 'column', md: 'row' }} 
                    alignItems={{ xs: 'flex-start', md: 'center' }} 
                    justifyContent="space-between" 
                    spacing={2}
                >
                    <Box>
                        <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 'bold', mb: 1 }}>
                            Document Management
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            {truck ? `Truck #${truck.unitNumber || truck.truckNumber || ''} - ${truck.make} ${truck.model}` : 'Truck Documents'}
                        </Typography>
                        <Chip 
                            label={`${documents.length} document${documents.length !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                mt: 1
                            }}
                        />
                    </Box>
                    
                    {!isMobile && canManageDocs && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            Add Document
                        </Button>
                    )}
                </Stack>
            </Paper>

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Filters and Controls */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Documents ({filteredDocuments.length})
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            variant={showFilters ? "contained" : "outlined"}
                        >
                            Filters
                        </Button>
                        <Button
                            size="small"
                            startIcon={viewMode === 'list' ? <ViewModuleIcon /> : <ViewListIcon />}
                            onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                            variant="outlined"
                        >
                            {viewMode === 'list' ? 'Grid' : 'List'}
                        </Button>
                    </Stack>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        sx={{ mb: 2 }}
                    >
                        <TextField
                            size="small"
                            placeholder="Search documents..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                            sx={{ flex: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filterStatus}
                                label="Status"
                                onChange={(e) => setFilterStatus(e.target.value)}
                            >
                                <MenuItem value="all">All Status</MenuItem>
                                <MenuItem value="VALID">Valid</MenuItem>
                                <MenuItem value="EXPIRING_SOON">Expiring Soon</MenuItem>
                                <MenuItem value="EXPIRED">Expired</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Document Type</InputLabel>
                            <Select
                                value={filterType}
                                label="Document Type"
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                {documentTypes.map(type => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Collapse>
            </Paper>

            {/* Documents Display */}
            {filteredDocuments.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                    <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No documents found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {documents.length === 0 
                            ? (canManageDocs
                                ? "Start by adding your first document for this truck."
                                : "No documents have been filed for this truck yet.")
                            : "Try adjusting your search or filter criteria."
                        }
                    </Typography>
                    {canManageDocs && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                        >
                            Add First Document
                        </Button>
                    )}
                </Paper>
            ) : (
                <Box>
                    {viewMode === 'list' ? (
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <List>
                                {filteredDocuments.map((document, index) => (
                                    <React.Fragment key={document._id}>
                                        <ListItem 
                                            sx={{ 
                                                py: 2,
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <Badge
                                                    color={statusColors[document.status] || 'default'}
                                                    variant="dot"
                                                    overlap="circular"
                                                >
                                                    {getDocumentIcon(document.fileReference)}
                                                </Badge>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                        {document.displayName}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Stack spacing={1} sx={{ mt: 1 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <Chip
                                                                size="small"
                                                                icon={statusIcons[document.status]}
                                                                label={document.status?.replace('_', ' ')}
                                                                color={statusColors[document.status] || 'default'}
                                                                sx={{ height: 24 }}
                                                            />
                                                            {!document.fileReference && (
                                                                <Chip size="small" label="No file" color="default" sx={{ height: 20 }} />
                                                            )}
                                                            {document.expiryDate && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Expires: {formatDate(document.expiryDate)}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                        {document.issuedBy && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Issued by: {document.issuedBy}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                }
                                                secondaryTypographyProps={{ component: 'div' }}
                                            />
                                            <ListItemSecondaryAction>
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title="View Document">
                                                        <IconButton size="small" onClick={() => handlePreview(document)}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {canManageDocs && (
                                                        <>
                                                            <Tooltip title="Edit Document">
                                                                <IconButton size="small" onClick={() => handleEdit(document)}>
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete Document">
                                                                <IconButton size="small" onClick={() => handleDeleteConfirm(document)}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Stack>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < filteredDocuments.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {filteredDocuments.map((document) => (
                                <Grid item xs={12} sm={6} md={4} key={document._id}>
                                    <Card 
                                        sx={{ 
                                            height: '100%',
                                            borderRadius: 2,
                                            transition: 'all 0.3s ease',
                                            '&:hover': { 
                                                transform: 'translateY(-4px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}
                                    >
                                        <CardHeader
                                            avatar={
                                                <Badge
                                                    color={statusColors[document.status] || 'default'}
                                                    variant="dot"
                                                    overlap="circular"
                                                >
                                                    {getDocumentIcon(document.fileReference)}
                                                </Badge>
                                            }
                                            title={
                                                <Typography variant="subtitle1" fontWeight={500} noWrap>
                                                    {document.displayName}
                                                </Typography>
                                            }
                                            subheader={
                                                <Chip
                                                    size="small"
                                                    icon={statusIcons[document.status]}
                                                    label={document.status?.replace('_', ' ')}
                                                    color={statusColors[document.status] || 'default'}
                                                    sx={{ mt: 0.5 }}
                                                />
                                            }
                                            action={
                                                canManageDocs ? (
                                                    <IconButton onClick={() => handleEdit(document)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                ) : null
                                            }
                                        />
                                        <CardContent sx={{ pt: 0 }}>
                                            <Stack spacing={1}>
                                                {document.expiryDate && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Expires: {formatDate(document.expiryDate)}
                                                    </Typography>
                                                )}
                                                {document.issuedBy && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Issued by: {document.issuedBy}
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                                    <Button size="small" onClick={() => handlePreview(document)}>
                                                        View
                                                    </Button>
                                                    {canManageDocs && (
                                                        <Button size="small" onClick={() => handleDeleteConfirm(document)}>
                                                            Delete
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            )}

            {/* Mobile FAB — office only */}
            {isMobile && canManageDocs && (
                <Fab
                    color="primary"
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                    onClick={handleAdd}
                >
                    <AddIcon />
                </Fab>
            )}

            {/* Enhanced Form Dialog with Stepper */}
            <Dialog 
                open={openForm} 
                onClose={() => setOpenForm(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
                fullScreen={isMobile}
                PaperProps={{
                    sx: { borderRadius: isMobile ? 0 : 3 }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            {formMode === 'add' ? 'Add New Document' : 'Edit Document'}
                        </Typography>
                        <IconButton onClick={() => setOpenForm(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pb: 8 }}>
                    <Box sx={{ mt: 2 }}>
                        {formMode === 'add' && (
                            <Stepper activeStep={activeStep} orientation={isMobile ? "vertical" : "horizontal"} sx={{ mb: 4 }}>
                                {steps.map((step, index) => (
                                    <Step key={step.label}>
                                        <StepLabel>{step.label}</StepLabel>
                                        {isMobile && (
                                            <StepContent>
                                                <Typography variant="body2" color="text.secondary">
                                                    {step.description}
                                                </Typography>
                                            </StepContent>
                                        )}
                                    </Step>
                                ))}
                            </Stepper>
                        )}

                        <form onSubmit={handleSubmit}>
                            {/* Step 0: Document Type Selection */}
            {(activeStep === 0 && formMode === 'add') && (
                                <Fade in timeout={300}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Select Document Type
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {documentTypes.map((type) => (
                                                <Grid item xs={12} sm={6} key={type.id}>
                                                    <Card
                                                        sx={{
                                                            cursor: 'pointer',
                                                            border: formData.documentType === type.id ? 2 : 1,
                                                            borderColor: formData.documentType === type.id 
                                                                ? 'primary.main' 
                                                                : 'divider',
                                                            '&:hover': { borderColor: 'primary.main' }
                                                        }}
                                                        onClick={() => setFormData(prev => ({ ...prev, documentType: type.id }))}
                                                    >
                                                        <CardContent>
                                                            <Stack direction="row" spacing={2} alignItems="center">
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="subtitle1" fontWeight={500}>
                                                                        {type.label}
                                                                    </Typography>
                                                                </Box>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                </Fade>
                            )}

                            {/* Step 1: Document Details */}
                            {(activeStep === 1 || formMode === 'edit') && (
                                <Fade in timeout={300}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Document Name"
                                                name="displayName"
                                                value={formData.displayName}
                                                onChange={handleChange}
                                                required
                                                helperText="Enter a descriptive name for this document"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    label="Issue Date"
                                                    value={formData.issueDate}
                                                    onChange={(date) => handleDateChange('issueDate', date)}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                <DatePicker
                                                    label="Expiry Date"
                                                    value={formData.expiryDate}
                                                    onChange={(date) => handleDateChange('expiryDate', date)}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Issued By"
                                                name="issuedBy"
                                                value={formData.issuedBy}
                                                onChange={handleChange}
                                                helperText="Authority or organization that issued this document"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Document Number"
                                                name="documentNumber"
                                                value={formData.documentNumber}
                                                onChange={handleChange}
                                                helperText="Reference or serial number"
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Notes"
                                                name="notes"
                                                value={formData.notes}
                                                onChange={handleChange}
                                                multiline
                                                rows={3}
                                                helperText="Additional notes or comments"
                                            />
                                        </Grid>
                                    </Grid>
                                </Fade>
                            )}

                            {/* Step 2: File Upload */}
                            {(activeStep === 2 || formMode === 'edit') && (
                                <Fade in timeout={300}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Upload Document File
                                        </Typography>
                                        
                                        {/* Drag and Drop Zone */}
                                        <Box
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            sx={{
                                                border: 2,
                                                borderStyle: 'dashed',
                                                borderColor: dragOver ? 'primary.main' : 'grey.300',
                                                borderRadius: 2,
                                                p: 4,
                                                textAlign: 'center',
                                                bgcolor: dragOver ? 'action.hover' : 'background.paper',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onClick={() => document.getElementById('file-input').click()}
                                        >
                                            <input
                                                id="file-input"
                                                type="file"
                                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                                onChange={handleFileChange}
                                                style={{ display: 'none' }}
                                            />
                                            
                                            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                                            
                                            {selectedFile ? (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        {selectedFile.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFile(null);
                                                        }}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Remove File
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        Drag and drop your file here
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        or click to browse
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Supported formats: PDF, DOC, DOCX, JPG, PNG (Max: 10MB)
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {formMode === 'edit' && !selectedFile && (
                                            <Alert severity="info" sx={{ mt: 2 }}>
                                                No new file selected. The existing file will be kept.
                                            </Alert>
                                        )}
                                    </Box>
                                </Fade>
                            )}
                        </form>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 0, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'space-between' }}>
                        <Box>
                            {formMode === 'add' && activeStep > 0 && (
                                <Button onClick={handleBack}>
                                    Back
                                </Button>
                            )}
                        </Box>
                        
                        <Stack direction="row" spacing={2}>
                            <Button onClick={() => setOpenForm(false)}>
                                Cancel
                            </Button>
                            
                            {formMode === 'add' && activeStep < steps.length - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={!isStepValid(activeStep)}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={submitting || (formMode === 'add' && !isStepValid(activeStep))}
                                    startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                                >
                                    {submitting ? 'Saving...' : 'Save Document'}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} scroll="paper">
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{deleteTarget?.displayName}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Document Preview */}
            <Dialog 
                open={previewOpen} 
                onClose={() => {
                    setPreviewOpen(false);
                    setPreviewUrl('');
                    setPreviewDocument(null);
                }}
                maxWidth="lg"
                fullWidth
                scroll="paper"
            >
                <DialogTitle>
                    {previewDocument?.displayName || previewDocument?.documentType || 'Document Preview'}
                </DialogTitle>
                <DialogContent dividers sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
                    {previewLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!previewLoading && !previewDocument?.fileReference && !previewDocument?.s3Key && (
                        <Typography color="text.secondary">No file available for this document.</Typography>
                    )}
                    {!previewLoading && previewUrl && (
                            <Box sx={{ width: '100%', height: { xs: 400, md: 600 } }}>
                                {isImageDoc(previewDocument, previewUrl) ? (
                                    <img
                                        src={previewUrl}
                                        alt={previewDocument.displayName || 'Document'}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        onError={(e) => console.warn('[Preview] Image failed to load', e)}
                                    />
                                ) : (
                                    <object
                                        data={previewUrl}
                                        type={previewDocument?.contentType || 'application/pdf'}
                                        width="100%"
                                        height="100%"
                                        onError={(e) => console.warn('[Preview] Object failed to load', e)}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Unable to embed this file type. Use Open / Download below.
                                        </Typography>
                                    </object>
                                )}
                            </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    {previewUrl && (
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={(e) => {
                                try {
                                    e.preventDefault();
                                    e.stopPropagation();
                                } catch (_) {}
                                try {
                                    window.open(previewUrl, '_blank', 'noopener,noreferrer');
                                } catch (err) {
                                    console.warn('[Preview] window.open failed:', err);
                                }
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            Open / Download
                        </Button>
                    )}
                    <Button onClick={() => {
                        setPreviewOpen(false);
                        setPreviewUrl('');
                        setPreviewDocument(null);
                    }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default TruckDocumentList; 